// Popup.js (Refactored to use new utils)
import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import "./popup.css";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";

import {
  formatDateTime,
  extractCaseNumber,
  extractCaseNumbers,
  stripHtml,
  validateCaseNumberFormat,
  stripUnknownClosingTags,
  trimLeadingEmptyBlocks,
  removeEmptyBlocksBeforeLists,
  normalizeWhitespaceInHtml,
  cleanHtmlForSubmit,
  removeEditorLineBreaks,
  unwrapListItemParagraphs,
} from "./utils";
import { saveNotesApi } from "./services/apiService";

// Normalise HTML before loading into TipTap/ProseMirror.
// The API returns lists like <ul>\n<li>A</li>\n</ul> — ProseMirror wraps each
// whitespace text node in a list_item, producing phantom bullets.
function sanitizeForTiptap(html) {
  if (!html) return html;
  try {
    const wrap = document.createElement("div");
    wrap.innerHTML = String(html);
    // 1. Strip whitespace-only text nodes inside <ul>/<ol>
    wrap.querySelectorAll("ul, ol").forEach((list) => {
      Array.from(list.childNodes).forEach((n) => {
        if (n.nodeType === Node.TEXT_NODE && !n.textContent.trim())
          list.removeChild(n);
      });
    });
    // 2. Remove genuinely empty <li>
    wrap.querySelectorAll("li").forEach((li) => {
      if (!li.textContent.replace(/ /g, "").trim())
        li.parentNode && li.parentNode.removeChild(li);
    });
    // 3. Remove <ul>/<ol> left with no children
    wrap.querySelectorAll("ul, ol").forEach((list) => {
      if (!list.querySelector("li"))
        list.parentNode && list.parentNode.removeChild(list);
    });
    // 4. Merge consecutive same-tag lists
    ["ul", "ol"].forEach((tag) => {
      Array.from(wrap.querySelectorAll(tag)).forEach((list) => {
        if (!list.parentNode) return;
        while (true) {
          let next = list.nextSibling;
          while (next && next.nodeType === Node.TEXT_NODE && !next.textContent.trim()) {
            const tmp = next.nextSibling;
            list.parentNode.removeChild(next);
            next = tmp;
          }
          if (next && next.nodeType === Node.ELEMENT_NODE && next.tagName.toLowerCase() === tag) {
            while (next.firstChild) list.appendChild(next.firstChild);
            next.parentNode.removeChild(next);
          } else break;
        }
      });
    });
    return wrap.innerHTML;
  } catch (_) {
    return String(html);
  }
}

function plainTextToHtml(text) {
  if (!text) return "";
  // Already HTML (saved by the TipTap editor in a previous session) — use directly.
  if (/<[a-z][\s\S]*>/i.test(text)) return text;
  return text.split("\n").map(line => `<p>${line || "<br>"}</p>`).join("");
}

// Maps doc.textContent character indices → ProseMirror document positions.
function buildPosMap(doc) {
  const map = [];
  doc.descendants((node, pos) => {
    if (node.isText) {
      for (let i = 0; i < node.text.length; i++) map.push(pos + i);
    }
    return true;
  });
  return map;
}

// Module-level constants so TipTap v3 compareOptions always sees the same
// references, preventing setOptions/view.setProps/view.updateState from firing
// on every render and swallowing typed input.
// Summary editor autosave timing.
// DEBOUNCE: quiet period after the last keystroke before saving.
// MAX_WAIT: hard ceiling since the last save attempt, so continuous typing
// still gets saved instead of deferring forever.
const SUMMARY_AUTOSAVE_DEBOUNCE_MS = 30000;
const SUMMARY_AUTOSAVE_MAX_WAIT_MS = 60000;

const TIPTAP_EXTENSIONS = [
  StarterKit,
  Highlight.configure({ multicolor: true }),
];
const TIPTAP_EDITOR_PROPS = {
  handleKeyDown(view, event) {
    if (event.key === "Tab") {
      // Inside a list item: let StarterKit handle Tab/Shift+Tab so it runs
      // sinkListItem / liftListItem to create or remove sub-bullets.
      const { $from } = view.state.selection;
      for (let d = $from.depth; d > 0; d--) {
        if ($from.node(d).type.name === "listItem") return false;
      }
      // Outside a list — insert spaces instead of tabbing away from the editor
      event.preventDefault();
      view.dispatch(view.state.tr.insertText("    "));
      return true;
    }
    return false;
  },
};

function TiptapToolbar({ editor }) {
  if (!editor) return null;
  const btn = (label, action, active) => (
    <button
      key={label}
      onMouseDown={(e) => { e.preventDefault(); action(); }}
      className={"tiptap-btn" + (active ? " active" : "")}
    >
      {label}
    </button>
  );
  return (
    <div className="tiptap-toolbar">
      {btn("B",  () => editor.chain().focus().toggleBold().run(),         editor.isActive("bold"))}
      {btn("I",  () => editor.chain().focus().toggleItalic().run(),       editor.isActive("italic"))}
      {btn("U",  () => editor.chain().focus().toggleUnderline().run(),    editor.isActive("underline"))}
      <span className="tiptap-divider" />
      {btn("1.", () => editor.chain().focus().toggleOrderedList().run(),  editor.isActive("orderedList"))}
      {btn("•",  () => editor.chain().focus().toggleBulletList().run(),   editor.isActive("bulletList"))}
      <span className="tiptap-divider" />
      {btn("↩",  () => editor.chain().focus().undo().run(), false)}
      {btn("↪",  () => editor.chain().focus().redo().run(), false)}
    </div>
  );
}

function TiptapEditorWrapper({ initialContent, onChange, editorRef, editable = true }) {
  // Freeze initial content so TipTap's compareOptions never sees a change.
  const stableContent = useRef(null);
  if (stableContent.current === null) {
    stableContent.current = sanitizeForTiptap(initialContent) || "";
  }
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });

  const editor = useEditor({
    extensions: TIPTAP_EXTENSIONS,
    content: stableContent.current,
    onUpdate: ({ editor }) => onChangeRef.current(editor.getHTML()),
    editorProps: TIPTAP_EDITOR_PROPS,
    editable,
  });

  useEffect(() => {
    if (editor) editor.setEditable(editable);
  }, [editor, editable]);

  useEffect(() => {
    editorRef.current = editor;
    return () => { editorRef.current = null; };
  }, [editor, editorRef]);

  return (
    <div className="tiptap-editor-root">
      {editable && <TiptapToolbar editor={editor} />}
      <EditorContent editor={editor} />
    </div>
  );
}


// One AI-guidance card. All three variants (callouts / follow-ups / notes)
// share this structure; only the colour class and the pill text differ.
// The backend sends plain text, so items render as JSX and React escapes them
// — deliberately not innerHTML, unlike the sample page this design came from.
function GuidanceCard({ variant, title, tag, items }) {
  return (
    <section className={`guidance-card guidance-${variant}`}>
      <div className="guidance-card-head">
        <span className="guidance-card-title">{title}</span>
        <span className="guidance-tag">{tag}</span>
      </div>
      <div className="guidance-card-body">
        {items.length ? (
          <ul className="guidance-list">
            {items.map((item, i) => {
              // Notes arrive as "Field: value" — bold just the field name.
              const idx = item.indexOf(":");
              const hasLabel = idx > 0 && idx <= 40;
              return (
                <li key={i}>
                  {hasLabel ? (
                    <>
                      <span className="guidance-note-label">{item.slice(0, idx)}:</span>
                      {" " + item.slice(idx + 1).trim()}
                    </>
                  ) : (
                    item
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="guidance-empty-line">Nothing captured yet.</p>
        )}
      </div>
    </section>
  );
}

export default function Popup({
  transcript,
  contactId,
  callStartTime,
  lastUpdated,
  summary,
  tab,
  staffId,
  loadingTranscript,
  loadingSummary,
  summaryFetchError,
  isEditing,
  editedSummary,
  setEditedSummary,
  setIsEditing,
  setSummary,
  getSummary,
  liveCallActive,
  callStatus,
  isLiveCall,
  onTabChange,
  initialCaseNumber = "",
  initialCaseName = "",
  onSubmitJournal,
  getCaseInfo,
  journalId,
  onAutoSaveEditedSummary,
  isSummaryGenerated,
  wsConnectingStatus,
  parentOriginOverride = "",
  notes: initialNotesRaw = "",
  followUpQuestions = "",
  interviewNotes = [],
  keyCallouts = [],
  caseMetadata = null,
}) {

  const parentOrigin = parentOriginOverride || "";

  // Tabs
  const [activeTab, setActiveTab] = useState(tab || "transcript");

  // Journal UI state
  const [showJournalModal, setShowJournalModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showActiveCallWarning, setShowActiveCallWarning] = useState(false);
  const [warningShownForCall, setWarningShownForCall] = useState(false);

  const [internalCaseNumber, setInternalCaseNumber] = useState(initialCaseNumber);
  const [caseName, setCaseName] = useState(initialCaseName);
  const [shortDescription, setShortDescription] = useState("");
  const [validated, setValidated] = useState(false);
  // If true, we bypass manual validation because case number+name came from query params
  const [autoValidated, setAutoValidated] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const [validating, setValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [journalError, setJournalError] = useState("");
  const [summaryError, setSummaryError] = useState("");

  // Agent Notes scratch pad
  const [notesContent, setNotesContent] = useState("");
  const [notesDirty, setNotesDirty] = useState(false);
  const [notesSaveStatus, setNotesSaveStatus] = useState("idle"); // idle | saving | saved | error
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesLocked, setNotesLocked] = useState(false);
  const notesLastSaved = useRef("");
  const notesTimerRef = useRef(null);
  const notesEditorInitial = useRef("");
  const [notesEditorKey, setNotesEditorKey] = useState(0);
  // The drawer auto-opens once per call, not once per transcript update. On a
  // live call `transcript` changes with every WS message, so without this latch
  // the auto-open effect reopens the drawer the agent just closed.
  const notesAutoOpenedRef = useRef(false);

  // Summary editor autosave
  const summaryAutoSaveTimerRef = useRef(null);
  const summaryLastSavedRef = useRef("");
  // When the current run of unsaved edits began (0 when in sync). Caps how long
  // the debounce can defer a save during uninterrupted typing, without making a
  // keystroke after a long idle pause save instantly.
  const summaryDirtySinceRef = useRef(0);
  // Last content an autosave actually committed to the backend, in display form.
  // Cancel restores this rather than the pre-edit text: autosave has already
  // persisted it server-side, so reverting the screen past it would show stale
  // content that a refetch would immediately contradict.
  const summaryLastAutoSavedDisplayRef = useRef(null);
  // Bumped whenever an edit session starts or ends (save, cancel, contact
  // change). An autosave whose request is still in flight compares against it
  // to tell that the editor moved on without it.
  const summaryEditSessionRef = useRef(0);
  // The session Cancel ended, so an autosave that lands afterwards can still
  // sync the screen to the content it committed.
  const summaryCancelledSessionRef = useRef(null);
  const [summaryAutoSaveStatus, setSummaryAutoSaveStatus] = useState("idle"); // idle | saving | saved | error

  // Find & Replace state
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [findText, setFindText] = useState("");
  const [replaceText, setReplaceText] = useState("");
  const [matchIndices, setMatchIndices] = useState([]);
  const [currentMatchIdx, setCurrentMatchIdx] = useState(-1);

  // Refs
  const transcriptRef = useRef(null);
  const summaryRef = useRef(null);
  const editorRef = useRef(null);
  const notesEditorRef = useRef(null);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);

  // remember summary before editing so we can restore (trimmed) on cancel
  const originalSummaryRef = useRef(null);

  // Ensure summary is fetched only once per contactId
  const summaryLoadedRef = useRef(false);

  // Reset the "summary fetched" flag when contact changes
  useEffect(() => {
    summaryLoadedRef.current = false;
  }, [contactId]);

  // Reset the warning flag when contact changes
  useEffect(() => {
    setWarningShownForCall(false);
  }, [contactId]);

  // Reset summary autosave state when contact changes, so an autosave left in
  // flight from the previous call cannot write onto the new call's summary.
  useEffect(() => {
    if (summaryAutoSaveTimerRef.current) clearTimeout(summaryAutoSaveTimerRef.current);
    summaryLastSavedRef.current = "";
    summaryDirtySinceRef.current = 0;
    summaryLastAutoSavedDisplayRef.current = null;
    summaryCancelledSessionRef.current = null;
    summaryEditSessionRef.current += 1;
    setSummaryAutoSaveStatus("idle");
  }, [contactId]);

  // Reset notes UI when contact changes
  useEffect(() => {
    setNotesContent("");
    notesLastSaved.current = "";
    notesEditorInitial.current = "";
    setNotesDirty(false);
    setNotesSaveStatus("idle");

    setNotesOpen(false);
    setNotesLocked(false);
    notesAutoOpenedRef.current = false;
    if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
    setNotesEditorKey(k => k + 1);
  }, [contactId]);

  // Load notes from API response when they arrive (comes after contactId is set)
  useEffect(() => {
    if (!initialNotesRaw) return;
    const html = plainTextToHtml(parseIncomingNotes(initialNotesRaw));
    notesEditorInitial.current = html;
    setNotesContent(html);
    notesLastSaved.current = html;
    setNotesDirty(false);
    setNotesSaveStatus("idle");
    setNotesEditorKey(k => k + 1);
  }, [initialNotesRaw]);

  // Auto-open notes drawer the first time the transcript arrives for this call.
  // Latched, so later transcript updates (every WS message on a live call) leave
  // the drawer however the agent last set it. Reset on contact change above.
  useEffect(() => {
    if (notesAutoOpenedRef.current) return;
    if (!loadingTranscript && (transcript || initialNotesRaw)) {
      notesAutoOpenedRef.current = true;
      setNotesOpen(true);
    }
  }, [loadingTranscript, transcript, initialNotesRaw]);

  // Lock notes when summary is generated
  useEffect(() => {
    if (isSummaryGenerated) setNotesLocked(true);
  }, [isSummaryGenerated]);

  // Auto-save every 30 seconds when content changes
  useEffect(() => {
    if (notesLocked) return;
    if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
    if (stripHtml(notesContent).trim() === stripHtml(notesLastSaved.current).trim()) return;
    setNotesDirty(true);
    notesTimerRef.current = setTimeout(() => {
      saveNotesToBackend(notesContent);
    }, 30000);
    return () => { if (notesTimerRef.current) clearTimeout(notesTimerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notesContent, notesLocked]);

  // Auto-save summary editor: 30s after typing stops, but never later than 60s
  // after the previous attempt while the agent keeps typing.
  useEffect(() => {
    if (!isEditing || !onAutoSaveEditedSummary) return;
    if (summaryAutoSaveTimerRef.current) clearTimeout(summaryAutoSaveTimerRef.current);
    if (editedSummary === summaryLastSavedRef.current) {
      // Back in sync with the backend; the next divergence starts a fresh run.
      summaryDirtySinceRef.current = 0;
      return;
    }
    setSummaryAutoSaveStatus("idle");
    if (!summaryDirtySinceRef.current) summaryDirtySinceRef.current = Date.now();
    const dirtyFor = Date.now() - summaryDirtySinceRef.current;
    const delay = Math.max(
      0,
      Math.min(SUMMARY_AUTOSAVE_DEBOUNCE_MS, SUMMARY_AUTOSAVE_MAX_WAIT_MS - dirtyFor)
    );
    summaryAutoSaveTimerRef.current = setTimeout(async () => {
      const session = summaryEditSessionRef.current;
      const rawHtml = editorRef.current ? editorRef.current.getHTML() : (editedSummary || "");
      const html = stripMarkTags(rawHtml);
      const plain = stripHtml(html);
      if (!plain || plain.trim() === "") return;
      const hasLists = /<[ou]l>|<li>/i.test(html);
      let sanitized = trimLeadingEmptyBlocks(html);
      sanitized = removeEmptyBlocksBeforeLists(sanitized);
      sanitized = normalizeWhitespaceInHtml(sanitized);
      sanitized = stripUnknownClosingTags(sanitized);
      if (!hasLists) sanitized = removeEditorLineBreaks(sanitized);
      sanitized = unwrapListItemParagraphs(sanitized);

      // Same cleanup the manual save uses for on-screen content, so a later
      // Cancel can restore exactly what the viewer would otherwise render.
      let forDisplay = trimLeadingEmptyBlocks((html || "").trim());
      forDisplay = removeEmptyBlocksBeforeLists(forDisplay);
      forDisplay = normalizeWhitespaceInHtml(forDisplay);
      if (!hasLists) forDisplay = removeEditorLineBreaks(forDisplay);

      setSummaryAutoSaveStatus("saving");
      try {
        await onAutoSaveEditedSummary(sanitized);
        if (summaryEditSessionRef.current !== session) {
          // The editor closed while this request was in flight. The content is
          // committed either way, so if Cancel was what closed it, sync the
          // screen instead of leaving the pre-edit text showing.
          if (summaryCancelledSessionRef.current === session) {
            summaryCancelledSessionRef.current = null;
            setSummary(forDisplay);
          }
          return;
        }
        summaryLastSavedRef.current = editedSummary;
        summaryLastAutoSavedDisplayRef.current = forDisplay;
        summaryDirtySinceRef.current = 0;
        setSummaryAutoSaveStatus("saved");
      } catch {
        if (summaryEditSessionRef.current !== session) return;
        // Leave summaryLastSavedRef untouched so this content is still treated
        // as unsaved and the next keystroke retries it, but restart the window
        // so a failing endpoint is not retried on every keystroke.
        summaryDirtySinceRef.current = Date.now();
        setSummaryAutoSaveStatus("error");
      }
    }, delay);
    return () => { if (summaryAutoSaveTimerRef.current) clearTimeout(summaryAutoSaveTimerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedSummary, isEditing]);

  // Restore fields when journal closes
  useEffect(() => {
    if (!showJournalModal) {
      setInternalCaseNumber(initialCaseNumber);
      setCaseName(initialCaseName);
      setValidated(false);
      setValidationMessage("");
      setShortDescription("");
      setJournalError("");
    }
  }, [showJournalModal, initialCaseNumber, initialCaseName]);

  // Tab change callback
  useEffect(() => {
    if (onTabChange) onTabChange(activeTab);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Transcript Auto-scroll
  useEffect(() => {
    const el = transcriptRef.current;
    if (!el) return;

    const handler = () => {
      const bottom = el.scrollHeight - el.scrollTop - el.clientHeight < 10;
      setAutoScrollEnabled(bottom);
    };

    el.addEventListener("scroll", handler);
    return () => el.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    if (activeTab === "transcript" && autoScrollEnabled && transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [transcript, activeTab, autoScrollEnabled]);

  // Load summary when summary tab is opened
useEffect(() => {
  if (
    activeTab === "summary" &&
    contactId &&
    !summaryLoadedRef.current
  ) {
    if (summary && summary.trim()) {
      summaryLoadedRef.current = true;
    } else if (!loadingSummary) {
      summaryLoadedRef.current = true;
      getSummary();   // normal summary fetch
    }
  }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [activeTab, contactId, loadingSummary, summary, isSummaryGenerated]);

  // Prefill case number when journal opens
  useEffect(() => {
    if (!showJournalModal) return;

    const html = isEditing ? editedSummary : summary;
    const extractedList = extractCaseNumbers(html);
    const extractedSingle = extractedList.length === 1 ? extractedList[0] : null;
    const multipleExtracted = extractedList.length > 1;

    const initialPresent = initialCaseNumber && initialCaseName;
    const summaryMatchesInitial = extractedSingle && initialPresent && extractedSingle === initialCaseNumber;

    if (multipleExtracted) {
      // Multiple case numbers in summary → leave blank for user to choose
      setInternalCaseNumber("");
      setCaseName("");
      setValidated(false);
      setAutoValidated(false);
      setValidationMessage("");
    } else if (initialPresent && (!extractedSingle || summaryMatchesInitial)) {
      // Query params case: auto-validate when summary has no case number or it matches
      setInternalCaseNumber(initialCaseNumber);
      setCaseName(initialCaseName);
      setValidated(true);
      setAutoValidated(true);
      setValidationMessage("");
    } else if (extractedSingle) {
      // Exactly one case number in summary
      setInternalCaseNumber(extractedSingle);
      setCaseName("");
      setValidated(false);
      setAutoValidated(false);
    } else {
      // No case number in summary — fall back to query params (without auto-validate)
      setInternalCaseNumber(initialCaseNumber);
      setCaseName(initialCaseName);
      setValidated(false);
      setAutoValidated(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showJournalModal]);

  // Find & Replace logic — TipTap/ProseMirror implementation
  function clearHighlights() {
    const editor = editorRef.current;
    if (!editor) return;
    const { state } = editor;
    const highlightMark = state.schema.marks.highlight;
    if (!highlightMark) return;
    // Pass the mark TYPE (not an instance) so all highlight colors are removed in one transaction.
    editor.view.dispatch(state.tr.removeMark(0, state.doc.content.size, highlightMark));
  }

  // Strip <mark> tags from HTML while preserving inner content — used as a
  // safety net before saving so highlights never reach the server.
  function stripMarkTags(html) {
    try {
      const div = document.createElement("div");
      div.innerHTML = html;
      div.querySelectorAll("mark").forEach((el) => {
        while (el.firstChild) el.parentNode.insertBefore(el.firstChild, el);
        el.parentNode.removeChild(el);
      });
      return div.innerHTML;
    } catch (_) {
      return html;
    }
  }

  function runFind(searchText) {
    const editor = editorRef.current;
    if (!editor || !searchText) {
      clearHighlights();
      setMatchIndices([]);
      setCurrentMatchIdx(-1);
      return;
    }
    // Must use doc.textContent (no block separators) to match buildPosMap's indexing.
    // editor.getText() adds "\n" between blocks which shifts every index after the first paragraph.
    const text = editor.state.doc.textContent;
    const posMap = buildPosMap(editor.state.doc);
    const results = [];
    let start = 0;
    const lowerText = text.toLowerCase();
    const lowerSearch = searchText.toLowerCase();
    while (start < lowerText.length) {
      const found = lowerText.indexOf(lowerSearch, start);
      if (found === -1) break;
      results.push({ index: found, length: searchText.length });
      start = found + 1;
    }
    clearHighlights();
    results.forEach((m, i) => {
      if (m.index + m.length - 1 >= posMap.length) return;
      const from = posMap[m.index];
      const to = posMap[m.index + m.length - 1] + 1;
      editor.chain().setTextSelection({ from, to }).setHighlight({ color: i === 0 ? "#ff9900" : "#ffff00" }).run();
    });
    setMatchIndices(results);
    setCurrentMatchIdx(results.length > 0 ? 0 : -1);
    if (results.length > 0 && results[0].index + results[0].length - 1 < posMap.length) {
      const from = posMap[results[0].index];
      const to = posMap[results[0].index + results[0].length - 1] + 1;
      editor.chain().setTextSelection({ from, to }).scrollIntoView().run();
    }
  }

  function navigateMatch(dir) {
    const editor = editorRef.current;
    if (!editor || !matchIndices.length) return;
    const posMap = buildPosMap(editor.state.doc);
    const curr = matchIndices[currentMatchIdx];
    if (curr && curr.index + curr.length - 1 < posMap.length) {
      const from = posMap[curr.index];
      const to = posMap[curr.index + curr.length - 1] + 1;
      editor.chain().setTextSelection({ from, to }).setHighlight({ color: "#ffff00" }).run();
    }
    const next = (currentMatchIdx + dir + matchIndices.length) % matchIndices.length;
    const nextM = matchIndices[next];
    if (nextM && nextM.index + nextM.length - 1 < posMap.length) {
      const from = posMap[nextM.index];
      const to = posMap[nextM.index + nextM.length - 1] + 1;
      editor.chain().setTextSelection({ from, to }).setHighlight({ color: "#ff9900" }).scrollIntoView().run();
    }
    setCurrentMatchIdx(next);
  }

  function replaceCurrent() {
    const editor = editorRef.current;
    if (currentMatchIdx === -1 || !matchIndices.length || !editor) return;
    const posMap = buildPosMap(editor.state.doc);
    const m = matchIndices[currentMatchIdx];
    if (m.index + m.length - 1 >= posMap.length) return;
    const from = posMap[m.index];
    const to = posMap[m.index + m.length - 1] + 1;
    // Use tr.insertText so replaceText is treated as plain text, not HTML
    editor.view.dispatch(editor.state.tr.insertText(replaceText, from, to));
    setEditedSummary(editor.getHTML());
    runFind(findText);
  }

  function replaceAll() {
    const editor = editorRef.current;
    if (!matchIndices.length || !editor) return;
    // Reverse order so later replacements don't shift positions of earlier ones.
    // Rebuild posMap each iteration since positions shift after each replacement.
    [...matchIndices].reverse().forEach((m) => {
      const posMap = buildPosMap(editor.state.doc);
      if (m.index + m.length - 1 >= posMap.length) return;
      const from = posMap[m.index];
      const to = posMap[m.index + m.length - 1] + 1;
      editor.view.dispatch(editor.state.tr.insertText(replaceText, from, to));
    });
    setEditedSummary(editor.getHTML());
    setMatchIndices([]);
    setCurrentMatchIdx(-1);
  }

  function closeFindReplace() {
    clearHighlights();
    setShowFindReplace(false);
    setFindText("");
    setReplaceText("");
    setMatchIndices([]);
    setCurrentMatchIdx(-1);
  }

  // Save Edited Summary
async function onSaveEditedSummary() {
  if (summaryAutoSaveTimerRef.current) clearTimeout(summaryAutoSaveTimerRef.current);
  try {
    // Clear highlights from editor, then read HTML and strip any residual <mark> tags
    clearHighlights();
    const rawHtml = editorRef.current ? editorRef.current.getHTML() : (editedSummary || "");
    const html = stripMarkTags(rawHtml);

    // Validate that summary is not empty
    const plain = stripHtml(html);
    if (!plain || plain.trim() === "") {
      setSummaryError(
        "Missing required information: The call summary was cleared. Restore or recreate the summary before proceeding."
      );
      return;
    }

    setSummaryError("");

    // 🔥 Detect lists from RAW html (source of truth)
    const hasLists = /<[ou]l>|<li>/i.test(html);

    // ----- DISPLAY CLEANUP -----
    // Trim leading empty blocks (Quill often inserts <p><br></p> before lists)
    let trimmedForDisplay = trimLeadingEmptyBlocks((html || "").trim());
    // remove any empty blocks that remain immediately before lists
    trimmedForDisplay = removeEmptyBlocksBeforeLists(trimmedForDisplay);
    // normalize whitespace globally to avoid accumulation of empty blocks
    trimmedForDisplay = normalizeWhitespaceInHtml(trimmedForDisplay);

    // Only remove editor line breaks if there are no lists (lists need their structure)
    const finalForDisplay = hasLists
      ? trimmedForDisplay
      : removeEditorLineBreaks(trimmedForDisplay);

    setSummary(finalForDisplay);
    // clear saved original since we've applied changes
    originalSummaryRef.current = null;

    const found = extractCaseNumber(plain);
    if (found) {
      setInternalCaseNumber(found);
      setValidated(false);
    }

    // ----- SAVE CLEANUP (AUTO-SAVE) -----
    if (onAutoSaveEditedSummary) {
      let sanitizedForSave = trimLeadingEmptyBlocks(html);
      sanitizedForSave = removeEmptyBlocksBeforeLists(sanitizedForSave);
      sanitizedForSave = normalizeWhitespaceInHtml(sanitizedForSave);
      sanitizedForSave = stripUnknownClosingTags(sanitizedForSave);

      if (!hasLists) {
        sanitizedForSave = removeEditorLineBreaks(sanitizedForSave);
      }

      // Strip TipTap's <p> wrappers inside <li> for CKEditor compatibility
      sanitizedForSave = unwrapListItemParagraphs(sanitizedForSave);

      await onAutoSaveEditedSummary(sanitizedForSave);
    }

    // Only close editor on successful save
    summaryLastSavedRef.current = editedSummary;
    summaryDirtySinceRef.current = 0;
    summaryLastAutoSavedDisplayRef.current = null;
    summaryCancelledSessionRef.current = null;
    summaryEditSessionRef.current += 1;
    setSummaryAutoSaveStatus("idle");
    closeFindReplace();
    setIsEditing(false);
  } catch (error) {
    // The save never reached the server. Keep the editor open (we skipped
    // setIsEditing(false) above) so the edit is not lost, and say so.
    setSummaryAutoSaveStatus("error");
    setSummaryError(
      "Unable to save the summary right now. Your changes are still here \u2014 please try Save again."
    );
  }
}

  // Cancel edit: discard unsaved changes and clear edited state.
  // Anything autosave already committed stays on screen: it lives in the
  // backend now, so the pre-edit text is no longer the truth to fall back to.
  function onCancelEdit() {
    if (summaryAutoSaveTimerRef.current) clearTimeout(summaryAutoSaveTimerRef.current);
    setSummaryAutoSaveStatus("idle");
    summaryLastSavedRef.current = "";
    summaryDirtySinceRef.current = 0;
    // Mark this session cancelled before bumping, so an autosave still in
    // flight knows to push its committed content onto the screen.
    summaryCancelledSessionRef.current = summaryEditSessionRef.current;
    summaryEditSessionRef.current += 1;
    const orig =
      summaryLastAutoSavedDisplayRef.current ?? originalSummaryRef.current ?? summary;
    summaryLastAutoSavedDisplayRef.current = null;
    let restored = trimLeadingEmptyBlocks(orig || "");
    restored = removeEmptyBlocksBeforeLists(restored);
    restored = normalizeWhitespaceInHtml(restored);
    // Check if content has lists before removing line breaks
    const hasLists = /<[ou]l>|<li>/.test(restored);
    // Only remove editor line breaks if there are no lists
    const finalRestored = hasLists ? restored : removeEditorLineBreaks(restored);
    setSummary(finalRestored);
    setEditedSummary("");
    closeFindReplace();
    setIsEditing(false);
    originalSummaryRef.current = null;
    setSummaryError("");
  }

  // Validate Case Number
  async function handleValidateCase() {
    // explicit user validation clears auto-validate
    setAutoValidated(false);
    const error = validateCaseNumberFormat(internalCaseNumber);
    if (error) {
      setValidationMessage(error);
      setValidated(false);
      return;
    }
    setValidating(true);

    try {
      const result = await getCaseInfo(internalCaseNumber.trim());

      if (!result || result?.raw?.errors?.length > 0) {
        const msg = result?.raw?.errors?.[0]?.errorDescription || "Case not found";
        setValidationMessage(msg);
        setValidated(false);
        return;
      }

      setCaseName(result.caseName);
      setValidated(true);
      setValidationMessage("");
    } catch {
      setValidationMessage("Validation failed");
      setValidated(false);
    } finally {
      setValidating(false);
    }
  }

  // Journal Submission
  async function handleJournalSubmit() {
    if (!(validated || autoValidated)) return setValidationMessage("Please validate the case number");
    if (!shortDescription.trim()) return setValidationMessage("Short description is required");

    // build cleaned HTML for final submit (works whether user edited or not)
    const longDescriptionRaw = isEditing ? editedSummary : summary;
    const cleaned = cleanHtmlForSubmit(longDescriptionRaw);
    // update displayed summary to cleaned version so UI reflects what is submitted
    // remove only editor-generated empty breaks for display (preserve backend breaks)
    setSummary(removeEditorLineBreaks(cleaned));

    const payload = {
      contactId,
      caseNumber: internalCaseNumber,
      caseName,
      shortDescription,
      longDescription: cleaned,
      staffId: staffId || null,
    };
    try {
      setIsSubmitting(true);
      setJournalError("");
      await onSubmitJournal(payload);

      // Clear auto-validated flag after submit
      setAutoValidated(false);
      setValidated(false);

      setShowJournalModal(false);
      setShowSuccessModal(true);
    } catch {
      setJournalError("Error processing journal. Please check the details and try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  // CAN SUBMIT?
  const canSubmit =
    (validated || autoValidated) &&
    internalCaseNumber.trim() &&
    caseName.trim() &&
    shortDescription.trim() &&
    !isSubmitting;


  // CLOSE POPUP (Iframe-safe with postMessage)
  function handleClosePopup(e) {
    if (e && e.stopPropagation) e.stopPropagation();

    // Primary method → notify parent container (OB)
    try {
      window.parent.postMessage(
        { type: "closeTab", source: "popup" },
        parentOrigin
      );
    } catch (err) {
      // ignore
    }

    //  Try closing if it was a standalone popup window
    try {
      if (window.opener) {
        window.close();
        return;
      }
    } catch (err) {
      // ignore
    }
  }


  function parseIncomingNotes(raw) {
    if (!raw) return "";
    try {
      const str = String(raw).trim();
      // Parse Python list string format: "['item1', 'item2']"
      if (str.startsWith("[") && str.endsWith("]")) {
        const matches = [...str.matchAll(/'((?:[^'\\]|\\.)*)'/g)];
        if (matches.length) return matches.map((m) => m[1]).join("\n");
      }
      return str;
    } catch {
      return String(raw);
    }
  }

  async function saveNotesToBackend(content) {
    if (notesLocked) return;
    setNotesSaveStatus("saving");
    try {
      await saveNotesApi(contactId, content);
      notesLastSaved.current = content;
      setNotesDirty(false);
      setNotesSaveStatus("saved");
    } catch (err) {
      console.error("Failed to save notes:", err);
      setNotesSaveStatus("error");
    }
  }

  function handleNotesChange(html) {
    setNotesContent(html);
    setNotesSaveStatus("idle");
  }

  function handleManualSave() {
    if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
    saveNotesToBackend(notesContent);
  }

  async function saveNotesIfDirty() {
    if (!notesDirty || notesLocked) return;
    if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
    await saveNotesToBackend(notesContent);
  }

  // ── AI interview guidance ─────────────────────────────────────────────
  const isGuidanceLive = Boolean(
    liveCallActive || isLiveCall || callStatus === "ACTIVE" || callStatus === "STARTED"
  );

  // follow_up_questions is one free-text field; the model sometimes returns
  // more than one question, newline-separated and occasionally bulleted.
  const followUpLines = String(followUpQuestions || "")
    .split(/\r?\n+/)
    .map((s) => s.replace(/^\s*[-•*]\s*/, "").trim())
    .filter(Boolean);

  const guidanceNotes = Array.isArray(interviewNotes) ? interviewNotes : [];
  const guidanceCallouts = Array.isArray(keyCallouts) ? keyCallouts : [];
  const hasGuidance =
    followUpLines.length > 0 || guidanceNotes.length > 0 || guidanceCallouts.length > 0;
  // Completed calls never receive suggestions, so the column is dropped
  // altogether and the transcript keeps the full width it has today.
  const showGuidance = isGuidanceLive || hasGuidance;

  // Popup JSX
  return (
    <div className="app-root">
    <div className="full-frame">
      <div className="agent-assist-label">Agent Assist</div>

      {/* MAIN CONTENT */}
      <div className="main-content main-content-wrapper">

        {/* TRANSCRIPT TAB */}
        <div className={`tab-panel transcript-tab ${activeTab === "transcript" ? "active" : "hidden"}`}>
          <div className={`transcript-split${notesOpen ? " drawer-open" : ""}`}>

            {showGuidance && (
              <aside className="guidance-column">
                <div className="guidance-header">
                  <span className="guidance-header-title">AI Interview Guidance</span>
                  {isGuidanceLive && <span className="guidance-header-live">— live</span>}
                </div>

                {caseMetadata && (
                  <div className="case-meta">
                    <div className="case-meta-item">
                      <span className="case-meta-label">Case ID</span>
                      <span className="case-meta-value">{caseMetadata.caseNumber}</span>
                    </div>
                    <div className="case-meta-item">
                      <span className="case-meta-label">Name</span>
                      <span className="case-meta-value">{caseMetadata.name}</span>
                    </div>
                    <div className="case-meta-item">
                      <span className="case-meta-label">Phone Number</span>
                      <span className="case-meta-value">{caseMetadata.phoneNumber}</span>
                    </div>
                    <div className="case-meta-item">
                      <span className="case-meta-label">Preferred Language</span>
                      <span className="case-meta-value">{caseMetadata.language}</span>
                    </div>
                  </div>
                )}

                {hasGuidance ? (
                  <div className="guidance-body">
                    {guidanceCallouts.length > 0 && (
                      <GuidanceCard
                        variant="callouts"
                        title="Key Callouts"
                        tag="IMPORTANT"
                        items={guidanceCallouts}
                      />
                    )}
                    <GuidanceCard
                      variant="followup"
                      title="Suggested follow-ups"
                      tag="ASK"
                      items={followUpLines}
                    />
                    <GuidanceCard
                      variant="notes"
                      title="Interview Status & Notes"
                      tag="CAPTURE"
                      items={guidanceNotes}
                    />
                  </div>
                ) : (
                  <div className="guidance-idle">
                    <div className="guidance-spinner" />
                    <div className="guidance-idle-title">Listening to the interview…</div>
                    <div className="guidance-idle-sub">
                      AI-generated follow-ups and interview notes will appear here.
                    </div>
                  </div>
                )}
              </aside>
            )}

            <div ref={transcriptRef} className="transcript-pane">

              {loadingTranscript || wsConnectingStatus === 'connecting' ? (
                <div>Connecting to live stream...</div>
              ) : wsConnectingStatus === 'connected' ? (
                <div>Transcript generation in progress...</div>
              ) : transcript ? (
                <div className="chat-container">
                  <div className="contact-id">ContactId: {contactId}</div>

                  {callStartTime && (
                    <div className="contact-id">Call Start Time: {formatDateTime(callStartTime)}</div>
                  )}

                  {transcript.split("\n").map((line, idx) => {
                    if (!line.trim()) return null;

                    const match = line.match(/^\[(\d{2}:\d{2}:\d{2})\]\s*(.*)$/);
                    const ts = match ? match[1] : null;
                    const rest = match ? match[2] : line;
                    const isAgent = rest.startsWith("Agent:");
                    const msg = rest.replace(/^(Agent:|Customer:)\s*/, "");

                    return (
                      <div key={idx} className={`chat-row ${isAgent ? "agent" : "customer"}`}>
                        <div className={`chat-bubble ${isAgent ? "agent" : "customer"}`}>
                          <span className={`speaker-label ${isAgent ? "agent" : "customer"}`}>
                            {isAgent ? "Agent" : "Customer"}
                          </span>
                          <span>{msg}</span>
                        </div>
                        {ts && <div className="chat-timestamp">{ts}</div>}
                      </div>
                    );
                  })}

                  {lastUpdated && (
                    <div className="contact-id">
                      {(liveCallActive || isLiveCall || callStatus === "ACTIVE")
                        ? "Last Updated"
                        : "Call End Time"}: {formatDateTime(lastUpdated)}
                    </div>
                  )}
                </div>
              ) : (
                <div>No transcript available</div>
              )}
            </div>

          </div>
        </div>

        {/* SUMMARY TAB */}
        <div
          ref={summaryRef}
          className={`tab-panel tab-panel-wrapper ${activeTab === "summary" ? "active" : "hidden"}`}
        >
          {loadingSummary ? (
            <div>Call Summary generation is in progress...</div>
          ) : summaryFetchError ? (
            <div className="error-message">{summaryFetchError}</div>
          ) : isEditing ? (
            <>
              {showFindReplace && (
                <div className="find-replace-bar">
                  <div className="find-replace-row">
                    <span className="fr-label">Find</span>
                    <input
                      className="find-replace-input"
                      placeholder="Search text…"
                      value={findText}
                      onChange={(e) => setFindText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") runFind(findText); }}
                      autoFocus
                    />
                    <button className="fr-btn fr-action" onClick={() => runFind(findText)}>Find</button>
                    <span className="find-replace-count">
                      {matchIndices.length > 0 ? `${currentMatchIdx + 1}/${matchIndices.length}` : ""}
                    </span>
                    <button className="fr-btn" onClick={() => navigateMatch(-1)} disabled={!matchIndices.length} title="Previous match">◀</button>
                    <button className="fr-btn" onClick={() => navigateMatch(1)}  disabled={!matchIndices.length} title="Next match">▶</button>
                  </div>
                  <div className="find-replace-row">
                    <span className="fr-label">Replace</span>
                    <input
                      className="find-replace-input"
                      placeholder="Replacement text…"
                      value={replaceText}
                      onChange={(e) => setReplaceText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") replaceCurrent(); }}
                    />
                    <button className="fr-btn fr-action" onClick={replaceCurrent} disabled={currentMatchIdx === -1}>Replace</button>
                    <button className="fr-btn fr-action" onClick={replaceAll} disabled={!matchIndices.length}>Replace All</button>
                  </div>
                </div>
              )}

              <div className="edit-wrapper">
                <TiptapEditorWrapper
                  initialContent={editedSummary || summary}
                  onChange={(html) => setEditedSummary(html)}
                  editorRef={editorRef}
                />
              </div>

              {summaryError && (
                <div className="error-message">{summaryError}</div>
              )}

              <div className="editor-buttons-fixed">
                <button
                  className={`fr-toggle-btn${showFindReplace ? " active" : ""}`}
                  onClick={() => showFindReplace ? closeFindReplace() : setShowFindReplace(true)}
                  title="Find & Replace"
                >
                  Find &amp; Replace
                </button>
                {summaryAutoSaveStatus !== "idle" && (
                  <span className={`editor-autosave-chip editor-autosave-${summaryAutoSaveStatus}`}>
                    {summaryAutoSaveStatus === "saving" && "Auto-saving…"}
                    {summaryAutoSaveStatus === "saved"  && "Auto-saved ✓"}
                    {summaryAutoSaveStatus === "error"  && "Auto-save failed"}
                  </span>
                )}
                <img src={require("./assets/save.gif")} alt="Save" onClick={onSaveEditedSummary} />
                <img src={require("./assets/cancel.gif")} alt="Cancel" onClick={onCancelEdit} />
              </div>
            </>
          ) : (
            <>
              <div className="summary-header">
                <div className="summary-title">Call Summary</div>
              </div>

              <div className="summary-content" dangerouslySetInnerHTML={{ __html: /<[ou]l>|<li>/.test(summary) ? summary : removeEditorLineBreaks(summary) }} />

              {!journalId && (
                <div className="summary-buttons">
                  <span className="ai-disclaimer">This is AI-generated content and can make mistakes. Please review and modify for accuracy.</span>
                  <img src={require("./assets/edit.gif")} alt="Edit" onClick={() => {
                    originalSummaryRef.current = summary;
                    summaryLastSavedRef.current = summary;
                    summaryDirtySinceRef.current = 0;
                    summaryLastAutoSavedDisplayRef.current = null;
                    summaryCancelledSessionRef.current = null;
                    summaryEditSessionRef.current += 1;
                    setSummaryAutoSaveStatus("idle");
                    setEditedSummary(summary);
                    setIsEditing(true);
                  }} />
                  <img src={require("./assets/journal-btn.gif")} alt="Journal" onClick={() => setShowJournalModal(true)} />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* TABS */}
      <div className="popup-tabs">
        <button className={`popup-tab-btn ${activeTab === "transcript" ? "active" : ""}`}
          onClick={() => setActiveTab("transcript")}
        >
          Call Assist
        </button>

        <button className={`popup-tab-btn ${activeTab === "summary" ? "active" : ""}`}
          disabled={false}
          onClick={async () => {
            const isLive =
              liveCallActive || callStatus === "ACTIVE" || callStatus === "STARTED";

            // 🔥 Only warn if:
            // 1. Call is live
            // 2. Summary is NOT already generated
            // 3. Warning not already shown
            if (isLive && !isSummaryGenerated && !warningShownForCall) {
              setShowActiveCallWarning(true);
            } else {
              await saveNotesIfDirty();
              setActiveTab("summary");
            }
          }}
        >
          Call Summary
        </button>

      </div>

      {/* ACTIVE CALL WARNING MODAL */}
      {showActiveCallWarning && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Warning: Call is Still Being Transcribed</h3>

            <p className="warning-message">
              Call is still being transcribed. You can proceed with creating the call summary, but please note you will not be able to generate another summary for this call. Please confirm if you would like to proceed.
            </p>

            <div className="modal-buttons">
              <img src={require("./assets/cancel.gif")} alt="Cancel"
                onClick={() => {
                  setShowActiveCallWarning(false);
                }}
              />

              <img
                src={require("./assets/continue.gif")}
                alt="Proceed"
                className="img-button-submit"
                onClick={async () => {
                  setShowActiveCallWarning(false);
                  setWarningShownForCall(true);
                  await saveNotesIfDirty();
                  setActiveTab("summary");
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* JOURNAL MODAL */}
      {showJournalModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Add Journal Entry</h3>

            <label>
              Case Number:<span style={{ color: "red" }}>*</span>
              <input
                type="text"
                value={internalCaseNumber}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^\d*$/.test(v)) {
                    setInternalCaseNumber(v);
                    setValidated(false);
                    setAutoValidated(false);
                  }
                }}
              />
            </label>

            <label>
              Case Name:<span style={{ color: "red" }}>*</span>
              <input type="text" value={caseName} onChange={(e) => {
                setCaseName(e.target.value);
                setValidated(false);
              }} />
            </label>

            <div className="modal-validation-group">
              <img
                src={require("./assets/validate.gif")}
                alt="Validate"
                onClick={handleValidateCase}
                className={`img-button-validate ${validating ? "disabled" : ""}`}
              />

              <div className={`validation-message ${validated ? "success" : "error"}`}>
                {validated ? `Validated ✔ (${caseName})` : validationMessage}
              </div>
            </div>

            <label>
              Short Description:<span style={{ color: "red" }}>*</span>
              <textarea
                disabled={!caseName}
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value.slice(0, 2000))}
              />
            </label>

            {journalError && (
              <div className="error-message">{journalError}</div>
            )}

            <div className="required-fields-note">
              <span style={{ color: "red" }}>*</span> - Indicates required fields
            </div>

            <div className="modal-buttons">
              <img src={require("./assets/cancel.gif")} alt="Cancel"
                onClick={() => {
                  setShowJournalModal(false);
                }}
              />

              <img
                src={require("./assets/submit.gif")}
                alt="Submit"
                className={!canSubmit ? "disabled img-button-submit" : "img-button-submit"}
                onClick={canSubmit ? handleJournalSubmit : undefined}
              />
            </div>
          </div>
        </div>
      )}

      {/* SUCCESS MODAL */}
      {showSuccessModal && (
        <div className="modal-overlay modal-overlay-blur" onClick={handleClosePopup}>
          <div
            className="modal-content modal-content-relative"
            onClick={(e) => e.stopPropagation()} // prevent overlay click when clicking inside modal
          >

            <span className="modal-close-btn" onClick={handleClosePopup}>✖</span>

            <h3 className="modal-success-title">Journal Submitted Successfully</h3>

            <button className="modal-close-button" onClick={handleClosePopup}>
              Close
            </button>

          </div>
        </div>
      )}

    </div>

    {/* Notes toggle — outside the main box, bottom-right corner */}
    <button
      className={`notes-tab-btn${notesOpen ? " active" : ""}${stripHtml(notesContent).trim() ? " has-notes" : ""}`}
      onClick={() => setNotesOpen((o) => !o)}
      title="Agent Notes"
    >
      ✎
    </button>

    {/* Notes slide-in drawer — sits beside full-frame, no overlap */}
    <div className={`notes-drawer${notesOpen ? " open" : ""}`}>

      {/* Header */}
      <div className="notes-side-header">
        <span className="notes-side-title">Agent Notes</span>
        <span className={`notes-status-chip notes-status-${notesSaveStatus}${notesDirty && notesSaveStatus === "idle" ? " notes-status-dirty" : ""}`}>
          {notesSaveStatus === "saving" && "Saving…"}
          {notesSaveStatus === "saved" && "Saved"}
          {notesSaveStatus === "error" && "Save failed"}
          {(notesSaveStatus === "idle" && notesDirty) && "Unsaved"}
        </span>
        <button className="notes-close-btn" onClick={() => setNotesOpen(false)} title="Close">✕</button>
      </div>

      {/* Locked banner */}
      {notesLocked && (
        <div className="notes-save-blocked-banner">
          <span className="notes-save-blocked-icon">⚠</span>
          <span>Summary generated - notes are now read-only. Please add or edit notes directly within the Call Summary</span>
        </div>
      )}

      {/* Scratch pad */}
      <div className={`notes-scratch-pad${notesLocked ? " locked" : ""}`}>
        <TiptapEditorWrapper
          key={notesEditorKey}
          initialContent={notesEditorInitial.current}
          onChange={handleNotesChange}
          editorRef={notesEditorRef}
          editable={!notesLocked}
        />
      </div>

      {/* Footer */}
      <div className="notes-side-footer">
        <button
          className="notes-save-all-btn"
          onClick={handleManualSave}
          disabled={!notesDirty || notesLocked}
          title={notesLocked ? "Notes locked after summary generation" : "Save now"}
        >
          Save
        </button>
      </div>
    </div>
    </div>
  );
}
